
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, mergeMap, switchMap, catchError } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import * as CareersActions from './careers.actions';
import { CareersService } from '../../services/careers.service';

@Injectable()
export class CareersEffects {
  private actions$ = inject(Actions);
  private careersService = inject(CareersService);

  private getErrorMessage(error: unknown): string {
    console.error('Careers API Error:', error);
    if (error instanceof HttpErrorResponse) {
      console.error('Error details:', error.error);
      if (error.status === 0) {
        return 'Unable to connect to the server. Please check your internet connection and try again.';
      }
      if (error.status === 400) {
        if (typeof error.error === 'string') {
          return error.error;
        }
        if (error.error?.message) {
          return error.error.message;
        }
        if (error.error?.errors) {
          const errors = Object.values(error.error.errors).flat().join(' ');
          return errors || 'Please check your information and try again.';
        }
        return 'Please check your information and try again.';
      }
      if (error.status === 500) {
        if (error.error?.message) {
          return `Server error: ${error.error.message}`;
        }
        return 'Server error. Please try again later.';
      }
      return error.error?.message || error.message || 'An error occurred. Please try again.';
    }
    return 'An unexpected error occurred. Please try again.';
  }

  uploadResume$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CareersActions.uploadResume),
      mergeMap(({ file }) =>
        this.careersService.uploadFile(file).pipe(
          map(resumeUrl => CareersActions.uploadResumeSuccess({ resumeUrl })),
          catchError(error =>
            of(CareersActions.uploadResumeFailure({ error: this.getErrorMessage(error) }))
          )
        )
      )
    )
  );

  // Atomically uploads the resume then submits the application — no store subscription needed
  uploadResumeAndSubmit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CareersActions.uploadResumeAndSubmit),
      switchMap(({ file, formData }) =>
        this.careersService.uploadFile(file).pipe(
          switchMap(resumeUrl => {
            const data: import('../../models/careers.model').CareerApplicationRequest = {
              ...formData,
              ResumeUrl: resumeUrl
            };
            return this.careersService.submitApplication(data).pipe(
              map(application => CareersActions.submitCareerApplicationSuccess({ application })),
              catchError(error =>
                of(CareersActions.submitCareerApplicationFailure({ error: this.getErrorMessage(error) }))
              )
            );
          }),
          catchError(error =>
            of(CareersActions.uploadResumeFailure({ error: this.getErrorMessage(error) }))
          )
        )
      )
    )
  );

  submitCareerApplication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CareersActions.submitCareerApplication),
      mergeMap(({ data }) =>
        this.careersService.submitApplication(data).pipe(
          map(application => CareersActions.submitCareerApplicationSuccess({ application })),
          catchError(error =>
            of(CareersActions.submitCareerApplicationFailure({ error: this.getErrorMessage(error) }))
          )
        )
      )
    )
  );

  getCareerApplications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CareersActions.getCareerApplications),
      mergeMap(({ pageNumber = 1, pageSize = 10 }) =>
        this.careersService.getApplications(pageNumber, pageSize).pipe(
          map(page => CareersActions.getCareerApplicationsSuccess({ page })),
          catchError(error =>
            of(CareersActions.getCareerApplicationsFailure({ error: this.getErrorMessage(error) }))
          )
        )
      )
    )
  );

  getCareerApplicationById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CareersActions.getCareerApplicationById),
      mergeMap(({ id }) =>
        this.careersService.getApplicationById(id).pipe(
          map(application => CareersActions.getCareerApplicationByIdSuccess({ application })),
          catchError(error =>
            of(CareersActions.getCareerApplicationByIdFailure({ error: this.getErrorMessage(error) }))
          )
        )
      )
    )
  );
}
